import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataTable, EmptyState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { BizStatusPill } from './StatusPill';
import { UsesCommonCell } from './UsesCommonCell';
import type { BizItem } from '../types/catalog';

interface BizTableProps {
  rows: BizItem[];
}

export function BizTable({ rows }: BizTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columns: DataTableColumn<BizItem>[] = [
    {
      key: 'feature',
      header: t('index.bizTable.columns.feature'),
      width: '200px',
      render: (row) => (
        <div>
          <div
            style={{
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {row.nameKo}
          </div>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            {row.feature}
          </div>
        </div>
      ),
    },
    {
      key: 'url',
      header: t('index.bizTable.columns.url'),
      width: '160px',
      render: (row) => {
        const isPlanned = row.status === 'PLANNED';
        if (isPlanned) {
          return (
            <span
              style={{
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-xs)',
                cursor: 'not-allowed',
              }}
            >
              {row.url}
            </span>
          );
        }
        return (
          <a
            href={row.url}
            onClick={(e) => {
              e.preventDefault();
              navigate(row.url);
            }}
            style={{
              color: 'var(--color-brand-primary)',
              textDecoration: 'none',
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
            }}
            aria-label={t('index.bizTable.navigate', { name: row.nameKo, url: row.url })}
          >
            {row.url}
          </a>
        );
      },
    },
    {
      key: 'status',
      header: t('index.bizTable.columns.status'),
      width: '100px',
      render: (row) => <BizStatusPill status={row.status} />,
    },
    {
      key: 'package',
      header: t('index.bizTable.columns.package'),
      render: (row) => (
        <span
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {row.package}
        </span>
      ),
    },
    {
      key: 'usesCommon',
      header: t('index.bizTable.columns.usesCommon'),
      width: '220px',
      render: (row) => <UsesCommonCell items={row.usesCommon} />,
    },
  ];

  if (rows.length === 0) {
    return (
      <EmptyState
        title={t('index.bizTable.empty.title')}
        description={t('index.bizTable.empty.description')}
      />
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.feature}
        rowStyle={(row) =>
          row.status === 'PLANNED'
            ? { opacity: 0.6, pointerEvents: 'none' }
            : undefined
        }
      />
    </div>
  );
}
